import { Link } from "react-router-dom";
import { AtSign, BellOff, CornerDownRight, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth-context";
import { useClientProfile } from "@/contexts/client-profile-context";
import { usePermissions } from "@/contexts/permission-context";
import { useChatRealtime } from "@/hooks/chat/use-chat-realtime";
import { canAskBrowserPermission, useChatBell, useChatSettings, useMarkChatNotificationsRead } from "@/hooks/chat/use-chat-notifications";
import { ROUTES } from "@/routes/paths";
import { cn } from "@/lib/utils";

const TIME = new Intl.DateTimeFormat("pt-PT", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" });

/** Com o chat ligado: abre a ligação única de tempo real do separador (avisos em qualquer página) e diz quantos há. */
export function useChatBellState(): { enabled: boolean; unread: number } {
  const { hasFeature } = useClientProfile();
  const { user } = useAuth();
  const { isAdmin, hasPermission } = usePermissions();
  // Sem a permissão do chat, nem ligação nem sino: o servidor recusaria tudo (e a ligação tentaria para sempre).
  const enabled = hasFeature("platform.chat") && !!user && !user.isSecurityAccount && (isAdmin || hasPermission("chat", "READ"));
  useChatRealtime(enabled, user?.id ?? null);
  const bell = useChatBell(enabled);
  return { enabled, unread: bell.data?.unread_count ?? 0 };
}

/** Os avisos do chat no sino da plataforma: menções, conversas diretas e respostas. Sem e-mail. */
export function ChatBellSection({ onNavigate }: { onNavigate: () => void }) {
  const bell = useChatBell(true);
  const settings = useChatSettings();
  const markRead = useMarkChatNotificationsRead();
  const items = bell.data?.items ?? [];
  const dnd = settings.data?.dnd_active ?? false;

  return (
    <section aria-label="Chat" className="border-b border-border/60">
      <div className="flex items-center justify-between px-4 pb-1 pt-3">
        <h3 className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">Chat</h3>
        <span className="flex items-center gap-2">
          {dnd && <span className="flex items-center gap-1 text-[11px] text-muted-foreground"><BellOff className="h-3 w-3" strokeWidth={1.75} aria-hidden />Não incomodar</span>}
          {(bell.data?.unread_count ?? 0) > 0 && (
            <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[11px]" onClick={() => markRead.mutate({})}>Marcar tudo como lido</Button>
          )}
        </span>
      </div>
      {canAskBrowserPermission() && (
        <p className="px-4 pb-2 text-[12px] text-muted-foreground">
          <button type="button" className="underline" onClick={() => void window.Notification.requestPermission()}>Ativar avisos do browser</button> para saberes das menções com o chat fechado.
        </p>
      )}
      {bell.isError && <p className="px-4 pb-3 text-[12px] text-destructive">Não foi possível carregar os avisos do chat.</p>}
      {!bell.isLoading && items.length === 0 && <p className="px-4 pb-3 text-[12px] text-muted-foreground">Sem menções nem mensagens novas.</p>}
      <ul className="max-h-64 overflow-y-auto pb-1">
        {items.slice(0, 8).map((n) => (
          <li key={n.id}>
            <Link
              to={`${ROUTES.CHAT}?c=${encodeURIComponent(n.conversation_id)}`}
              onClick={() => {
                if (!n.read) markRead.mutate({ ids: [n.id] });
                onNavigate();
              }}
              className={cn("flex gap-2.5 px-4 py-2 hover:bg-foreground/[0.035]", !n.read && "bg-foreground/[0.02]")}
            >
              {n.kind === "mencao" ? <AtSign className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} aria-label="Menção" /> : n.kind === "resposta" ? <CornerDownRight className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} aria-label="Resposta" /> : <MessageCircle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} aria-label="Conversa direta" />}
              <span className="flex min-w-0 flex-1 flex-col">
                <span className={cn("truncate text-[13px]", !n.read && "font-medium")}>{n.author_name ?? "Alguém"} · {n.conversation_title}</span>
                <span className="truncate text-[12px] text-muted-foreground">{n.snippet}</span>
              </span>
              <time dateTime={n.created_at} className="shrink-0 text-[11px] text-muted-foreground">{TIME.format(new Date(n.created_at))}</time>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
